import { ZoomCursor } from './zoom-cursor'
import { ModalComponent } from '../../components/modal/modal'
import { Nullable } from '../../types/common'
import { isMobile } from '../../utils/check-media'
import { isEscKey } from '../../utils/key'
import { CarouselComponent } from '../../components/carousel/carousel'
import { $el, $list, whenDefined } from 'src/scripts/utils/utils'

const CAROUSEL_SELECTOR = `[data-product-modal-carousel]`
const CAROUSEL_BUTTONS_SELECTOR = `[data-product-modal-carousel-button]`
const CAROUSEL_SLIDE_SELECTOR = `[data-product-modal-slide]`
const CAROUSEL_SELECTED_SLIDE_NUMBER_SELECTOR = `[data-product-modal-selected-slide-number]`
const ZOOM_CURSOR_SELECTOR = `zoom-cursor`

export class ProductModal extends ModalComponent {
  carousel: Nullable<CarouselComponent>
  mediaIndexMap: any
  isZoomed: boolean = false
  zoomedImage: any

  handleKeydown = (event: KeyboardEvent): void => {
    if (isEscKey(event)) {
      this.isZoomed ? this.zoomedImage.reset() : this.hide()
    }
  }

  handleCarouselInit = (): void => {
    this.updateCarouselDraggableState()
  }

  handleCarouselSelect = (): void => {
    this.updateSelectedSlideNumber()
  }

  openAndShowMediaById(mediaId) {
    this.show()

    if (!this.carousel) {
      return
    }

    const mediaIndex = this.mediaIndexMap[mediaId]

    this.carousel.embla.scrollTo(mediaIndex, true)

    const carouselButtons = $list(CAROUSEL_BUTTONS_SELECTOR, this.carousel)
    if (carouselButtons[0]) {
      carouselButtons[0].focus()
    }

    this.updateSelectedSlideNumber()
  }

  initCarousel(): void {
    this.mediaIndexMap = this.createMediaIndexMap()

    if (this.element) {
      this.carousel = $el(CAROUSEL_SELECTOR, this.element)
    }

    this.carousel?.embla.on('select', this.handleCarouselSelect)
    this.carousel?.embla.on('init', this.handleCarouselInit)
  }

  resetCarousel(): void {
    this.carousel?.embla.off('select', this.handleCarouselSelect)
    this.carousel?.embla.off('init', this.handleCarouselInit)
  }

  hide(): any {
    this.resetCarousel()
    super.hide()
    this.isZoomed = false
    this.updateModal()
    this.emit('hide', {})
  }

  show(): any {
    super.show()
    this.initCarousel()
  }

  updateSelectedSlideNumber() {
    const selectedSlideNumber = $el(
      CAROUSEL_SELECTED_SLIDE_NUMBER_SELECTOR,
      this.element,
    )

    if (!selectedSlideNumber || !this.carousel) {
      return
    }

    const selectedIndex = this.carousel.embla.selectedScrollSnap()
    const newSlideNumber = selectedIndex + 1

    selectedSlideNumber.textContent = String(newSlideNumber)
  }

  updateModal(): void {
    this.updateCarouselDraggableState()
    this.updateZoomCursorState()
    this.updateZoomState()
  }

  createMediaIndexMap() {
    const slides = $list(CAROUSEL_SLIDE_SELECTOR, this.element)

    return slides.reduce((acc, slide, index) => {
      if (!slide.dataset.mediaId) {
        return acc
      }

      return { ...acc, [slide.dataset.mediaId]: index }
    }, {})
  }

  updateCarouselDraggableState() {
    if (!this.carousel) {
      return
    }

    if (!isMobile()) {
      this.carousel.reInit({ watchDrag: false })

      return
    }

    this.carousel.reInit({ watchDrag: !this.isZoomed })
  }

  updateZoomCursorState(): void {
    const zoomCursor: ZoomCursor = $el(ZOOM_CURSOR_SELECTOR)

    if (!zoomCursor) {
      return
    }

    zoomCursor.updateState(this.isZoomed)
  }

  updateZoomState(): void {
    this.toggleAttribute('zoom-enabled', this.isZoomed)
  }
}
